import React, { useEffect, useState } from 'react';
import { Col, Row } from 'antd';
import { NextPage, NextPageContext } from 'next';
import { customFormatMessage, fetchInitialData, isFirstScan } from 'utils';
import ConsumableCoupon from 'src/components/consumables/consumableCoupon';
import {
  ConsumablePointsOperatorEnum,
  CouponTypeEnum,
  PlatformEnum,
} from 'src/shared/enums';
import LayoutUserProfile from 'src/components/layout/UserProfile';
import { privateRoute } from 'services/privateRoute';
import apiPaths from 'services/apiPaths';
import {
  ICustomerBonusResponse,
  IMyCouponsItem,
} from 'src/components/account/account.interface';
import Link from 'src/components/Link';
import routePaths from 'config/routes';
import { LeftOutlined } from '@ant-design/icons';
import { FormattedMessage } from 'react-intl';
import { config } from 'config/config';
import ConsumableSpecialDiscoveryCoupon from 'src/components/consumables/consumableSpecialCoupon';
import { IRootReducers } from 'src/reducers';
import { ICampaign } from 'src/shared/models';
import { connect } from 'react-redux';
import { AnyAction, Dispatch, bindActionCreators } from 'redux';
import { setUserCampaigns } from 'src/actions/appActions';
import getConfig from 'next/config';
import { authTypes } from 'src/types/authTypes';

const { DEFAULT_TIMEZONE } = getConfig().publicRuntimeConfig;

type Props = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps> &
  ICustomerBonusResponse;

const UserBonusPage: NextPage<Props> = ({
  userCampaigns,
  userCoupons,
  setUserCampaigns,
}): JSX.Element => {
  const [emptyState, setEmptyState] = useState(true);
  const [discoveryCoupon, setDiscoveryCoupon] = useState([]);

  const faqIcon = `${config.APP.MEDIA_CON}InterrogationIcon.png`;
  const { PLATFORM } = config.APP;
  let srcNoCouponImg = `${config.APP.MEDIA_CON}`;
  const getNoCouponImageByCountry = () => {
    switch (PLATFORM) {
      case PlatformEnum.CON_DEUTSCHLAND:
        srcNoCouponImg = srcNoCouponImg.concat('no_coupon_image_DE.png');
        break;
      case PlatformEnum.CON_BRAZIL:
        srcNoCouponImg = srcNoCouponImg.concat('no_coupon_image_BR.png');
        break;
      case PlatformEnum.CON_ITALY:
        srcNoCouponImg = srcNoCouponImg.concat('no_coupon_image_IT.png');
        break;
      case PlatformEnum.CON_FRANCE:
        srcNoCouponImg = srcNoCouponImg.concat('no_coupon_image_FR.png');
        break;
      default:
        srcNoCouponImg = srcNoCouponImg.concat('no_coupon_image.png');
        break;
    }
    return srcNoCouponImg;
  };

  const checkCampaigns = (campaign: ICampaign): boolean => {
    if (!campaign) return false;
    else {
      const timezone = parseInt(DEFAULT_TIMEZONE) * 60 * 60 * 10;
      const checkExpirationDate: boolean =
        new Date(campaign.expirationDate).getTime() - timezone <
        new Date().getTime();
      if (checkExpirationDate) return false;
    }
    return true;
  };

  useEffect(() => {
    setUserCampaigns();
  }, []);

  useEffect(() => {
    {
      setEmptyState(!userCoupons || userCoupons.length === 0);
    }
  }, [userCoupons]);

  useEffect(() => {
    setShowSpecialCouponEmptyState(checkCampaigns(userCampaigns));
  }, [userCampaigns]);

  const [showSpecialCouponEmptyState, setShowSpecialCouponEmptyState] =
    useState(checkCampaigns(userCampaigns));

  const getFAQsContent = () => {
    return (
      <Row
        justify="center"
        className={`${
          (!emptyState || showSpecialCouponEmptyState) &&
          'yellow-container-styles'
        }`}>
        <div
          className={`bonus-faqs-container ${
            (!emptyState || showSpecialCouponEmptyState) &&
            'bonus-faqs-container__yellow'
          }`}>
          <div className="consumable-modal-summary-yellow-ticket-icon consumable-modal-summary-yellow-ticket-icon__myCoupons">
            <img src={faqIcon}></img>
          </div>
          <div className="bonus-faqs-text-container bonus-faqs-text-container__title">
            <h3 className="consumables-modal-yellow-title consumables-modal-yellow-title__myCoupons">
              {customFormatMessage('page.account.my-bonus.yellow-block-title')}
            </h3>
          </div>
          <div
            className={`${
              emptyState && !showSpecialCouponEmptyState
                ? 'no-yellow-subtitle'
                : 'yellow-subtitle'
            }`}>
            <p className="bonus-faqs-subtitle-text">
              {customFormatMessage(
                'page.account-my-bonus.yellow-block-subtitle',
              )}
            </p>
          </div>
        </div>
      </Row>
    );
  };

  const getContent = () => {
    if (emptyState && !showSpecialCouponEmptyState) {
      return (
        <Row justify="center">
          <Col className="bonus-not-available">
            <Row justify="center">
              <img
                className="no-coupon-image"
                src={getNoCouponImageByCountry()}
              />
            </Row>
            <Row justify="center">
              <p className="bonus-not-available__title">
                {customFormatMessage('page.account.my-bonus.no-bonus')}
              </p>
            </Row>
            <Row justify="center" style={{ marginTop: '-3%' }}>
              <p className="bonus-not-available__subtitle">
                {customFormatMessage('page.account.my-bonus.no-bonus.subtitle')}
              </p>
            </Row>
          </Col>
        </Row>
      );
    }

    return (
      <div className="bonus-available">
        <div className="bonus-available__title my-bonus-title-desktop">
          {customFormatMessage('menu.section.personal.coupons')}
        </div>
        <div className="bonus-available__subtitle">
          {customFormatMessage('page.account.my-bonus.bonus.subtitle')}
        </div>
        <div className="bonus-cards">
          {checkCampaigns(userCampaigns) && (
            <div className="bonus-cards__item special-bonus-card">
              <ConsumableSpecialDiscoveryCoupon
                campaign={userCampaigns}
                discoveryCoupons={discoveryCoupon}
              />
            </div>
          )}
          {!emptyState &&
            userCoupons.map((coupon, index: number) => {
              const firstScan = isFirstScan(coupon);
              return (
                <div key={index} className="bonus-cards__item">
                  <ConsumableCoupon
                    consumableName={coupon.name}
                    type={coupon.type}
                    consumableImg={coupon.img}
                    couponFromLanding={true}
                    consumablePoints={coupon.consumablePoints}
                    bonusPoints={coupon.points}
                    operation={coupon.pointsOperator}
                    expiration={coupon.expiration}
                    myCoupons={true}
                    firstScan={firstScan}
                    campaignType={coupon.campaignType}
                    hasFamily={coupon.hasFamily}
                  />
                </div>
              );
            })}
        </div>
      </div>
    );
  };

  return (
    <>
      <LayoutUserProfile customClassName={'accountPointsPage'}>
        <Link href={routePaths.PAGES.MY_ACCOUNT.ACCOUNT_INDEX}>
          <Row className="button-custom-back" align="middle">
            <LeftOutlined />
            <FormattedMessage id="menu.header.my-account" />
          </Row>
        </Link>
        {(!emptyState || showSpecialCouponEmptyState) && (
          <div className="accountPage__title accountPage__title--my-points section-title--big accountPage__title--my-bonus-title accountPage__title--my-coupons-title">
            <i className="icon icon--ticket icon-top accountPage__title--my-bonus-icon" />
            <FormattedMessage id="menu.section.personal.coupons" />
          </div>
        )}
        {getContent()}
        {getFAQsContent()}
      </LayoutUserProfile>
    </>
  );
};

const initialAction = async (
  accessToken: string,
  ctx: NextPageContext,
): Promise<{ data }> => {
  try {
    const customerCoupons = await fetchInitialData(
      accessToken,
      `${apiPaths.CONSUMABLES.GET_ALL_CUSTOMER_COUPONS}`,
      ctx,
    );

    const orderedCoupons = getCouponsItemListOrdered(customerCoupons);

    ctx.store.dispatch({
      type: authTypes.AUTH_UPDATE_USER_COUPONS,
      payload: { userCoupons: orderedCoupons || [] },
    });
    return { data: orderedCoupons };
  } catch (error) {
    console.error('SERVER ERROR: ', error);
    return { data: null };
  }
};

export const getCouponsItemListOrdered = (
  customerCoupons,
): IMyCouponsItem[] => {
  const { consumables, coupons } = customerCoupons;

  const couponsToShow: IMyCouponsItem[] = consumables.flatMap((consumable) => {
    const relatedCoupons = coupons.filter(
      (coupon) => coupon.consumableId === consumable.id,
    );

    const parent =
      consumable.parentId &&
      consumables.find((c) => c.id === consumable.parentId);

    return relatedCoupons.map((coupon) => ({
      ...coupon,
      img: parent?.contents?.image ?? consumable.contents?.image,
      name: parent?.contents?.name ?? consumable.contents?.name,
      consumablePoints: coupon.consumableScanPoints,
      bonusPoints: consumable.points.bonusPoints,
      hasFamily: coupon.hasFamily,
    }));
  });

  if (couponsToShow.length) {
    couponsToShow.sort((a, b) => {
      if (a.expiration > b.expiration) return 1;
      if (a.expiration < b.expiration) return -1;
      return 0;
    });
  }

  return couponsToShow;
};

const mapStateToProps = (state: IRootReducers) => {
  return {
    userCampaigns: state.app.userCampaigns,
    userCoupons: state.auth.userCoupons,
  };
};

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) =>
  bindActionCreators({ setUserCampaigns }, dispatch);

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  privateRoute({
    WrappedComponent: UserBonusPage,
    initialAction,
  }),
);
